import { t } from 'i18next';
import { css, html, LitElement, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { iconProductImage } from '@/components/icons';
import {
  BREAKPOINT_EXTRA_LARGE_PX,
  BREAKPOINT_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
} from '@/settings';
import { baseStyles, formStyles, rootVariablesStyles } from '@/styles';
import { SelectOption, SelectVersion } from '@/types';

import '@/components/form/fg-select';

@customElement('fg-product-download')
export class FgProductDownload extends LitElement {
  @state()
  selectedColors: string[] | 'all' = 'all';

  @state()
  selectedResolution = 'all';

  @property({ type: String })
  downloadLink?: string;

  @property({ type: String })
  downloadButtonLabel = 'labels.downloadButton';

  @property({ type: String })
  downloadTitle = '';

  @property({ type: Array })
  colors: SelectOption[] = [];

  @property({ type: Array })
  resolutions: SelectOption[] = [];

  @property({ type: Object })
  icon: TemplateResult<1> = iconProductImage;

  private onChangeColors(newSelectedColors: string[]) {
    this.selectedColors =
      newSelectedColors?.length > 0 ? newSelectedColors : 'all';
  }

  private onChangeResolution(event: CustomEvent<{ value: string }>) {
    this.selectedResolution = event.detail?.value;
  }

  perTranslateResolutionData(): SelectOption[] {
    return [
      ...this.resolutions.map(resolution => {
        return {
          value: resolution?.value,
          text: t(resolution?.text),
        };
      }),
    ];
  }

  private downloadItem() {
    this.dispatchEvent(
      new CustomEvent('download-file', {
        detail: {
          hasColor: this.colors?.length > 0,
          colors:
            this.selectedColors === 'all'
              ? this.colors?.map(color => color?.value)
              : this.selectedColors,
          resolution: this.selectedResolution,
        },
      }),
    );
  }

  private showColorSelect() {
    if (this.colors?.length > 0) {
      return html`
        <fg-select
          part="wholesale"
          name="color-select"
          .version=${SelectVersion.MULTI_SELECT}
          .multiSelectTranslationKey=${'labels.selectedColor'}
          .placeholder=${'labels.allColors'}
          .options=${this.colors}
          @options-selected=${(event: CustomEvent<{ value: string[] }>) =>
            this.onChangeColors(event.detail.value)}
          class="select"
          largeDropdown
        ></fg-select>
      `;
    }
  }

  private showResolutionSelect() {
    if (this.resolutions?.length > 0) {
      return html`
        <fg-select
          part="wholesale"
          name="resolution-select"
          .version=${SelectVersion.SINGLE_SELECT}
          .defaultValue=${this.selectedResolution}
          .options=${this.perTranslateResolutionData()}
          @options-selected=${(event: CustomEvent<{ value: string }>) =>
            this.onChangeResolution(event)}
          class="select"
        ></fg-select>
      `;
    }
  }

  private showSelectsContainer() {
    if (this.resolutions?.length > 0 || this.colors?.length > 0) {
      return html`
        <div class="selects-container">
          ${this.showColorSelect()} ${this.showResolutionSelect()}
        </div>
      `;
    }
  }

  private renderLinkOrButton() {
    if (this.downloadLink !== undefined) {
      return html`
        <a href=${this.downloadLink} target="_blank" class="download">
          ${t(this.downloadButtonLabel)}
        </a>
      `;
    }

    return html`
      <button @click=${this.downloadItem} class="download">
        ${t(this.downloadButtonLabel)}
      </button>
    `;
  }

  render() {
    return html`
      <div class="download-block">
        <div class="icon-title-container">
          <div class="icon-container">${this.icon}</div>
          <span class="title">${t(this.downloadTitle)}</span>
        </div>
        <div class="actions-container">
          ${this.showSelectsContainer()} ${this.renderLinkOrButton()}
        </div>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    formStyles,
    rootVariablesStyles,
    css`
      .download-block {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        height: 100px;
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        margin-top: 8px;

        @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
          && {
            gap: 16px;
          }
        }
      }

      .icon-pdf-wrapper svg {
        width: 100%;
        height: 100%;
      }

      .icon-pdf-wrapper {
        height: 35px;
        width: 31px;
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      .icon-container {
        background-color: #ffffff;
        width: 88px;
        height: 88px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 8px;
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        flex-shrink: 0;
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      .icon-title-container {
        display: flex;
        flex-direction: row;
        align-items: center;
      }

      .title {
        margin-left: 8px;
        font-size: var(--fg-font-size--xl);
        font-weight: 600;
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
      }

      .selects-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        height: 100%;
        gap: 16px;
      }

      .actions-container {
        display: flex;
        height: auto;
        align-items: center;

        @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
          gap: 24px;
          margin-right: 24px;
        }

        @media screen and (max-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
          && {
            flex-direction: column;
            justify-content: center;
            align-items: end;
            margin-right: 8px;
          }
        }
      }

      .select {
        width: 240px;
        @media screen and (max-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
          && {
            margin-top: 8px;
          }
        }
        @media screen and (max-width: ${BREAKPOINT_LARGE_PX + 200}px) {
          && {
            width: 200px;
          }
        }
      }

      .download {
        /* margin-right: 24px; */
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        font-size: 16px;
        font-weight: 600;
        text-decoration: none;

        @media screen and (max-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
          && {
            margin-top: 8px;
            margin-bottom: 8px;
          }
        }
      }
    `,
  ];
}
